import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { Spinner, Container } from 'react-bootstrap';
import { RootState, AppDispatch } from '../../store';
import { setCredentials, setImpersonation, setTimesheetReports } from '../../store/slices/authSlice';
import { useAuthUser } from '../../hooks/useAuthUser';
import DashboardLayout from '../dashboard/DashboardLayout';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRoles = [] }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { token } = useSelector((state: RootState) => state.auth);

  // Use React Query to fetch user (with deduplication)
  const { data: user, realUser, timesheetReports, isLoading, isError } = useAuthUser();

  // Sync React Query user data with Redux store
  useEffect(() => {
    if (user && token) {
      dispatch(setCredentials({ user, token }));
      dispatch(setImpersonation({
        realUser: realUser ?? null,
        isImpersonating: !!realUser,
      }));
      dispatch(setTimesheetReports(timesheetReports));
    }
  }, [user, token, realUser, timesheetReports, dispatch]);

  // Show loading spinner while fetching user
  if (isLoading) {
    return (
      <Container fluid className="vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Loading...</div>
        </div>
      </Container>
    );
  }

  // If no token or failed to fetch user, redirect to login
  if (!token || isError || !user) {
    // Store the current location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Block users with "none" role from accessing any page except profile
  if (user.role === 'none' && location.pathname !== '/profile') {
    return (
      <DashboardLayout
        title="Pending Approval"
        subtitle="Your account is awaiting administrator approval"
      >
        <div className="text-center py-5">
          <i className="bi bi-hourglass-split fs-1 text-warning mb-3 d-block"></i>
          <h3 className="text-warning mb-3">Account Pending Approval</h3>
          <p className="text-muted mb-4">
            Your account has been created but does not yet have access to any features.
            <br />
            An administrator has been notified and will assign you appropriate permissions soon.
            <br />
            <br />
            Please check back later or contact your administrator if you have any questions.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <a href="/profile" className="btn btn-outline-primary">
              <i className="bi bi-person me-2"></i>
              View Profile
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Check role-based access
  if (requiredRoles.length > 0 && user.role && !requiredRoles.includes(user.role)) {
    return (
      <DashboardLayout
        title="Access Denied"
        subtitle="You don't have permission to access this page"
      >
        <div className="text-center py-5">
          <i className="bi bi-shield-exclamation fs-1 text-danger mb-3 d-block"></i>
          <h3 className="text-danger mb-3">Access Denied</h3>
          <p className="text-muted mb-4">
            You don't have the required permissions to view this page.
            <br />
            Please contact your administrator if you believe this is an error.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <a href="/dashboard" className="btn btn-primary">
              <i className="bi bi-house me-2"></i>
              Go to Dashboard
            </a>
            <a href="/profile" className="btn btn-outline-secondary">
              <i className="bi bi-person me-2"></i>
              View Profile
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
